import { Theme } from './types';

export const commonColors: Record<string, string> = {
  white: '#FFFFFF',
  black: '#121212',
  orange: '#FE4F40',
  red: '#f1536e',
  green: '#0C8782',
  yellow: '#cc8800',
  primaryDarkBlue: '#112331',
  cyanBlue: '#40FAF0',
};

export const iconColors: Record<string, string> = {
  primaryIcon: '#112331',
  greenIcon: '#00C764',
  yellowIcon: '#ffb400',
  blueIcon: '#16b1ff',
  grayIcon: '#829DA3',
  redIcon: '#FE4F40',
  violetIcon: '#8c57ff',
  turquoiseicon: '#00E0C8',
  oceanblueIcon: '#0C8782',
  cyanBlueIcon: '#40FAF0',
};

export const tableStatusColors: Record<string, string> = {
  lightRed: '#edabab',
  lightGreen: '#d9f3be',
  lightYellow: '#d1b65f4f',
  lightBlue: '#b3dbf1',
  blue: '#146ca4',
};

export const wipStatusColors: Record<string, string> = {
  // Invoice Pending
  wipInvoicePendingBg: '#fff3cd',
  wipInvoicePendingText: '#664d00',
  // Part Invoiced
  wipPartInvoicedBg: '#d4edda',
  wipPartInvoicedText: '#155724',
  // Add Trade in Analytics
  wipAddTradeBg: '#cce5ff',
  wipAddTradeText: '#004085',
  // Add Debt/Hedge Info
  wipAddDebtBg: '#f8d7da',
  wipAddDebtText: '#721c24',
  // Deal Summary Pending
  wipDealSummaryBg: '#ffe8cc',
  wipDealSummaryText: '#7c3700',
  // Proposal Pending
  wipProposalBg: '#e8e0f5',
  wipProposalText: '#2d1357',
  // Default / fallback
  wipDefaultBg: '#e0e8ec',
  wipDefaultText: '#2a3f4a',
};

export const graphColors: Record<string, string> = {
  primaryDarkBlueGraph: '#112331',
  purpleGraph: '#4700FF',
  greyGraph: '#879cA2',
  oceanBlueGraph: '#0C8782',
  orangeGraph: '#FE4F40',
  cyanBlueGraph: '#40FAF0',
  secondaryBlueGraph: '#0070FF',
  turquoiseGraph: '#00E0C8',
  lightPinkGraph: '#FF7AFF',
  pinkGraph: '#da3ddae0',
  darkBlueGraph: '#2C3E50',
  carrotOrangeGraph: '#E67E22',
  amethystGraph: '#9B59B6',
  blueGraph: '#3498DB',
  tealGraph: '#1ABC9C',
  brightRedGraph: '#E74C3C',
  orangeYellowGraph: '#F39C12',
  nepritisGraph: '#27AE60',
  wisteriaGraph: '#8E44AD',
  greenGraph: '#00C764',
  greenSeaGraph: '#16A085',
  pumpkinGraph: '#D35400',
  lightRedGraph: '#FF6B6B',
  lightGreyGraph: '#E5E7EB',
};

const typography = {
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  displayFontFamily: 'Playfair Display, sans-serif',
  fontSize: 16,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  headingFontSize: {
    h1: '2.5rem',
    h2: '2rem',
    h3: '1.75rem',
    h4: '1.5rem',
    h5: '1.25rem',
    h6: '1rem',
  },
  bodyFontSize: {
    body1: '1rem',
    body2: '0.875rem',
    caption: '0.75rem',
  },
};

const spacing = {
  unit: 8,
  xs: '0.25rem', // 4px
  sm: '0.5rem', // 8px
  md: '1rem', // 16px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
  xxl: '2.5rem', // 40px
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },
  borderRadius: {
    xs: '0.25rem', // 4px
    sm: '0.5rem', // 8px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
    round: '50%',
  },
};

const colors = {
  dark: {
    background: '#112331',
    paper: '#212e3f',
    divider: '#cccccc1f',
    //buttons
    primary: '#FE4F40',
    primaryDark: '#e44739',
    primaryLight: '#de5a4e',
    //icon color for all centers & cards
    cardIcons: '#FE4F40',
    //hover elements background
    secondaryBackground: '#283444',
    surfacePrimary: '#1C1C1C',
    //active element like tabs
    surfaceSecondary: '#2c3746',
    //for text
    textPrimary: '#fffffff2',
    textSecondary: '#a5adb8',
    textTertiary: '#2A4558',
    //white text for both dark/light mode
    textWhite: '#fffffff2',
    //
    grey: '#444346',
    boxShadow: '#4f617a',
    //links
    linkBlue: '#63a7ff',
    // table headers
    primaryTableHeaderBg: '#112331',
    primaryTableHeaderText: '#ffffff',
    // custom alert
    warnAlertBg: '#112331',
    warnAlertText: '#ffffff',

    success: commonColors.green,
    danger: '#E74C3C', // brightRedGraph
    // caveat rather than failure; matches --warningColor in globals.css
    warning: '#ffbc2b',

    // search highlight color - top nav
    searchHighlight: '#40FAF0',
    successOutlinedButton: '#40FAF0',

    ...commonColors,
    ...iconColors,
    ...tableStatusColors,
    ...wipStatusColors,
    ...graphColors,
  },
  light: {
    background: '#f4f4f4',
    paper: '#FFFFFF',
    divider: '#2e263d1f',
    //buttons
    primary: '#1d384c',
    primaryDark: '#112331',
    primaryLight: '#1d384cd1',
    //icon color for all centers & cards
    cardIcons: '#FFFFFF',
    //hover elements background
    secondaryBackground: '#ebecec',
    surfacePrimary: '#acb1ba',
    //active element like tabs
    surfaceSecondary: '#E8E8E8',
    //for text
    textPrimary: '#2A3547',
    textSecondary: '#2e263dcc',
    textTertiary: '#2A4558',
    //white text for both dark/light mode
    textWhite: '#fffffff2',
    //
    grey: '#888',
    boxShadow: '#919eab',
    //links
    linkBlue: '#0066CC',
    // table headers
    primaryTableHeaderBg: '#e6f3f2',
    primaryTableHeaderText: '#0c888a',
    // custom alert
    warnAlertBg: '#fff4e5',
    warnAlertText: '#663c0a',

    success: commonColors.green,
    danger: '#E74C3C',
    // darker than the dark-mode amber so 11px text stays readable on white
    warning: '#8a5a00',

    // search highlight color - top nav
    searchHighlight: '#0C8782',
    successOutlinedButton: '#1d384c',

    ...commonColors,
    ...iconColors,
    ...tableStatusColors,
    ...wipStatusColors,
    ...graphColors,
  },
};

const theme: Theme = {
  dark: {
    colors: colors.dark,
    typography,
    spacing,
    images: {},
  },
  light: {
    colors: colors.light,
    typography,
    spacing,
    images: {},
  },
};

export default theme;
